// src/components/messaging/CollaborationSignals.tsx

import { useCollaborationSignals } from '../../hooks/useCollaborationSignals';

interface CollaborationSignalsProps {
  threadId: string;
  workspaceId: string;
  userId?: string;
}

export default function CollaborationSignals({
  threadId,
  workspaceId,
  userId,
}: CollaborationSignalsProps) {
  const { typingUsers, usersOnCall, usersListeningVoicemail } = useCollaborationSignals({
    workspaceId,
    threadId,
    userId,
    enabled: !!threadId && !!workspaceId,
  });

  // Don't render if no active signals
  if (typingUsers.length === 0 && usersOnCall.length === 0 && usersListeningVoicemail.length === 0) {
    return null;
  }

  const formatNames = (users: Array<{ userFullName: string }>) => {
    if (users.length === 0) return '';
    if (users.length === 1) return users[0].userFullName;
    if (users.length === 2) return `${users[0].userFullName} and ${users[1].userFullName}`;
    return `${users[0].userFullName} and ${users.length - 1} others`;
  };

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-100 text-sm">
      {/* Typing indicator */}
      {typingUsers.length > 0 && (
        <div className="flex items-center gap-2 text-gray-600">
          <div className="flex space-x-1">
            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          <span>
            <span className="font-medium">{formatNames(typingUsers)}</span>
            {' '}is typing...
          </span>
        </div>
      )}

      {/* On call indicator */}
      {usersOnCall.length > 0 && (
        <div className="flex items-center gap-2 text-green-600">
          <div className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
          </div>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          <span>
            <span className="font-medium">{formatNames(usersOnCall)}</span>
            {' '}is on a call
          </span>
        </div>
      )}

      {/* Listening to voicemail indicator */}
      {usersListeningVoicemail.length > 0 && (
        <div className="flex items-center gap-2 text-purple-600">
          <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
            />
          </svg>
          <span>
            <span className="font-medium">{formatNames(usersListeningVoicemail)}</span>
            {' '}is listening to voicemail
          </span>
        </div>
      )}
    </div>
  );
}
